import { SendIcon } from 'lucide-react';
import { useCallback, type KeyboardEvent, type ReactElement } from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import { useChatBranding, useChatConfig, useChatStore } from '../ChatProvider';
import { useChatActions } from '../hooks/useChatActions';
import { AudioRecorderButton, AudioRecorderStatusStrip, useAudioRecorderUI } from './AudioRecorder';

const NEAR_LIMIT_REMAINING = 50;
const CRITICAL_LIMIT_REMAINING = 20;

export function ChatInput(): ReactElement {
  const branding = useChatBranding();
  const config = useChatConfig();
  const text = useChatStore((s) => s.inputText);
  const setText = useChatStore((s) => s.setInputText);
  const isStreaming = useChatStore((s) => s.streaming.status === 'streaming');
  const { sendText } = useChatActions();
  const recorder = useAudioRecorderUI();

  const limit = branding.charLimit;
  const remaining = limit - text.length;
  const overLimit = text.length > limit;
  const tone: 'ok' | 'warn' | 'danger' =
    overLimit || remaining <= CRITICAL_LIMIT_REMAINING
      ? 'danger'
      : remaining <= NEAR_LIMIT_REMAINING
        ? 'warn'
        : 'ok';

  const canSend = text.trim().length > 0 && !overLimit && !isStreaming;

  const handleSubmit = useCallback(() => {
    if (!canSend) return;
    void sendText(text);
  }, [canSend, sendText, text]);

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit],
  );

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
      className="px-5 pt-4 pb-[max(env(safe-area-inset-bottom),1.25rem)]"
      aria-label="Caixa de mensagem"
      data-slot="chat-input"
    >
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-3">
        {config.enableAudio && <AudioRecorderStatusStrip ui={recorder} />}

        {config.enableCharCounter && (
          <div
            aria-hidden
            className={cn(
              'self-end text-[10.5px] font-medium tabular-nums transition-colors',
              tone === 'ok' && 'text-muted-foreground/70',
              tone === 'warn' && 'text-warning',
              tone === 'danger' && 'text-destructive',
            )}
          >
            {text.length} / {limit}
          </div>
        )}
        <span aria-live="polite" className="sr-only">
          {tone === 'danger' && overLimit
            ? `Limite de caracteres excedido: ${text.length} de ${limit}.`
            : tone === 'danger'
              ? `Atenção: restam ${remaining} caracteres.`
              : tone === 'warn'
                ? `Aproximando do limite: restam ${remaining} caracteres.`
                : ''}
        </span>

        <div
          className={cn(
            'bg-muted/50 flex items-center gap-1.5 rounded-full px-2 py-1.5 transition-all',
            tone === 'warn' && 'focus-within:ring-warning/30 focus-within:ring-3',
            tone === 'danger' && 'focus-within:ring-destructive/30 focus-within:ring-3',
          )}
        >
          {config.enableAudio && <AudioRecorderButton ui={recorder} disabled={isStreaming} />}

          <input
            type="text"
            value={text}
            onChange={(e) => {
              setText(e.target.value);
            }}
            onKeyDown={onKeyDown}
            placeholder={branding.inputPlaceholder}
            aria-label="Mensagem"
            autoComplete="off"
            className="placeholder:text-muted-foreground/80 min-w-0 flex-1 bg-transparent px-3 py-2.5 text-sm outline-none"
          />

          <Button
            type="submit"
            size="icon"
            variant="ghost"
            aria-label="Enviar mensagem"
            disabled={!canSend}
            className="text-muted-foreground hover:text-foreground size-9 shrink-0 rounded-full"
          >
            <SendIcon aria-hidden className="size-4 -translate-x-px translate-y-px" />
          </Button>
        </div>
      </div>
    </form>
  );
}
